import { Link, createFileRoute } from '@tanstack/react-router'
import { format } from 'date-fns'
import { useState } from 'react'
import { GetDistributionCampaignsRequest_Query } from 'schema/gen/es/chiikipoint/adminconsole/v2/service_pb'
import Add from '~icons/material-symbols/add.svg'
import { css } from '../../../../../styled-system/css'
import { LoadingOverlay } from '../../../../components/loading-overlay'
import { TablePaginationToolbar } from '../../../../components/table-pagination-toolbar'
import { useGetAllFundSources, useGetDistributionCampaigns } from '../../../../libs/admin/hooks'
import { button } from '../../../../styles/buttonStyles'
import { contentWrapper } from '../../../../styles/contentWrapper'
import { pageTitle, titleWrapper } from '../../../../styles/sectionStyles'
import { table, tableCell, tableHead, tableHeadCell, tableRow, tableWrapper } from '../../../../styles/tableStyles'
import { CSVDownloader, QRCodeDownloader } from './-download-codes/downloader'

export const Route = createFileRoute('/admin/points/$fundSourceId/')({
  component: RouteComponent,
})

const useData = () => {
  const { fundSourceId } = Route.useParams()
  const query = new GetDistributionCampaignsRequest_Query({ fundSourceId })
  const { data: campaigns, isLoading: isCampaignsLoading, error: campaignsError } = useGetDistributionCampaigns({ query })
  const { data: fundSources, isLoading: isFundSourceLoading, error: fundSourceError } = useGetAllFundSources()
  const fundSource = fundSources?.find(fs => fs.id === fundSourceId)
  return {
    campaigns,
    fundSource,
    isLoading: isCampaignsLoading || isFundSourceLoading,
    error: campaignsError || fundSourceError,
  }
}

const BreadCrumb = () => {
  const { fundSource } = useData()

  if (!fundSource) {
    return null
  }

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        position: 'absolute',
        top: '0',
        left: '0',
      })}
    >
      <Link
        to="/admin/points"
        className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}
      >
        ポイント種類
      </Link>
      <span
        className={css({
          color: 'text.tertiary',
        })}
      >
        /
      </span>
      <span
        className={css({
          color: 'text.tertiary',
        })}
      >
        {fundSource.name}
      </span>
    </div>
  )
}

const TableContent = () => {
  const { campaigns, fundSource, isLoading } = useData()
  const sortedCampaigns = (campaigns ?? []).sort((a, b) => (Number(b.createdAt?.seconds) ?? 0) - (Number(a.createdAt?.seconds) ?? 0) )

  const [page, setPage] = useState(0)
  const itemsPerPage = 25
  const totalPages = Math.ceil((sortedCampaigns?.length ?? 0) / itemsPerPage)
  const startIndex = page * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedCampaigns = sortedCampaigns?.slice(startIndex, endIndex) ?? []

  return (
    <div className={tableWrapper()}>
      <TablePaginationToolbar
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
      <table className={table()}>
        <thead className={tableHead()}>
          <tr>
            <th className={tableHeadCell()}>キャンペーン名</th>
            <th className={tableHeadCell()}>配布ポイント</th>
            <th className={tableHeadCell()}>作成数</th>
            <th className={tableHeadCell()}>受け取り数</th>
            <th className={tableHeadCell()}>合計配布ポイント</th>
            <th className={tableHeadCell()}>ポイント有効期限</th>
            <th className={tableHeadCell()} />
          </tr>
        </thead>
        <tbody>
          {sortedCampaigns && fundSource && paginatedCampaigns.map(campaign => {
            const formattedExpirationDate = campaign.balanceExpiresAt ? format(campaign.balanceExpiresAt?.toDate(), 'yyyy年MM月dd日') : '未設定'
            const totalDistributionAmount = campaign.distributionBalanceAmount * campaign.distributionCount
            return (
              <tr key={campaign.id} className={tableRow()}>
                <td className={tableCell()}>{campaign.title}</td>
                <td className={tableCell()}>{campaign.distributionBalanceAmount.toLocaleString()}pt</td>
                <td className={tableCell()}>{campaign.distributionCount.toLocaleString()}</td>
                <td className={tableCell()}>{campaign.consumedCount.toLocaleString()}</td>
                <td className={tableCell()}>{totalDistributionAmount.toLocaleString()}&nbsp;pts</td>
                <td className={tableCell()}>{formattedExpirationDate}</td>
                <td className={tableCell()}>
                  <div className={css({
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                  })}>
                    <CSVDownloader
                      filename={`${fundSource.name}-${campaign.title}`}
                      campaignId={campaign.id}
                    />
                    <QRCodeDownloader
                      filename={`${fundSource.name}-${campaign.title}`}
                      campaignId={campaign.id}
                    />
                  </div>
                </td>
              </tr>
            )
          })}
          {!sortedCampaigns?.length && !isLoading && (
            <tr className={tableRow()}>
              <td colSpan={7} className={css({
                textAlign: 'center',
                padding: '16px',
                color: 'text.tertiary',
              })}>
                キャンペーンがありません
              </td>
            </tr>
          )}
          {isLoading && (
            <tr className={tableRow()}>
              <td colSpan={7} className={css({
                textAlign: 'center',
                padding: '16px',
                color: 'text.tertiary',
              })}>
                読み込み中
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {isLoading && <LoadingOverlay />}
    </div>
  )
}

function RouteComponent() {
  const { fundSourceId } = Route.useParams()

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>配布ポイント作成履歴</h1>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        position: 'relative',
      })}>
        <BreadCrumb />
        <div className={css({
          display: 'flex',
          width: '100%',
          justifyContent: 'flex-end',
        })}>
          <Link
            to="/admin/points/$fundSourceId/newCampaign"
            params={{ fundSourceId }}
          >
            <button
              type='button'
              className={button({ color: 'primary' })}
              onClick={() => { }}
            >
              <Add />
              配布ポイント作成
            </button>
          </Link>
        </div>
        <TableContent />
      </div>
    </div>
  )
}
